import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, RefreshCw, Send, X, Settings2, TrendingDown, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  price_drop: { label: 'Price Drop', icon: TrendingDown, cls: 'bg-amber-100 text-amber-700' },
  check_in: { label: 'Check-In', icon: MessageCircle, cls: 'bg-blue-100 text-blue-700' },
};

export default function Nudges() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [nudges, setNudges] = useState([]);
  const [settings, setSettings] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const [n, s] = await Promise.all([
        axios.get(`${API}/nudges?status=${tab}`, { withCredentials: true }),
        axios.get(`${API}/nudges/settings`, { withCredentials: true }),
      ]);
      setNudges(n.data || []);
      setSettings(prev => prev || s.data);
    } catch (e) {
      toast.error('Failed to load nudges');
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    try {
      setScanning(true);
      const { data } = await axios.post(`${API}/nudges/scan`, {}, { withCredentials: true });
      toast.success(`Scan complete — ${data.created} new nudge${data.created === 1 ? '' : 's'}, ${data.pending} pending`);
      setTab('pending');
      load();
    } catch (e) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      const { data } = await axios.patch(`${API}/nudges/settings`, {
        price_drop_days: parseInt(settings.price_drop_days),
        check_in_days: parseInt(settings.check_in_days),
      }, { withCredentials: true });
      setSettings(data);
      toast.success('Nudge timing saved');
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const approve = async (n) => {
    if (confirmingId !== n.id) {
      setConfirmingId(n.id);
      setTimeout(() => setConfirmingId(prev => (prev === n.id ? null : prev)), 4000);
      return;
    }
    setConfirmingId(null);
    try {
      setBusyId(n.id);
      await axios.post(`${API}/nudges/${n.id}/approve`, { message: drafts[n.id] ?? n.message }, { withCredentials: true });
      toast.success(`SMS sent to ${n.seller_name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (n) => {
    try {
      setBusyId(n.id);
      await axios.post(`${API}/nudges/${n.id}/dismiss`, {}, { withCredentials: true });
      toast.success('Dismissed');
      load();
    } catch (e) {
      toast.error('Failed to dismiss');
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '';

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#474747] flex items-center gap-2" data-testid="nudges-title">
            <BellRing className="w-6 h-6" /> Smart Nudges
          </h1>
          <p className="text-sm text-gray-500">Items live too long get a drafted seller SMS — nothing sends until you approve it.</p>
        </div>
        <Button onClick={scan} disabled={scanning} className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-semibold" data-testid="scan-nudges-button">
          <RefreshCw className={`w-4 h-4 mr-2 ${scanning ? 'animate-spin' : ''}`} /> {scanning ? 'Scanning…' : 'Scan Live Items'}
        </Button>
      </div>

      {settings && (
        <div className="bg-white rounded-xl border border-[#E9E9E9] p-4 mb-6 flex flex-wrap items-end gap-4" data-testid="nudge-settings-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#474747]"><Settings2 className="w-4 h-4" /> Timing rules</div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Price-drop nudge after (days live)</p>
            <Input type="number" min="1" className="w-28" value={settings.price_drop_days}
              onChange={(e) => setSettings({ ...settings, price_drop_days: e.target.value })} data-testid="price-drop-days-input" />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Check-in nudge after (days live)</p>
            <Input type="number" min="1" className="w-28" value={settings.check_in_days}
              onChange={(e) => setSettings({ ...settings, check_in_days: e.target.value })} data-testid="check-in-days-input" />
          </div>
          <Button size="sm" variant="outline" onClick={saveSettings} disabled={savingSettings} data-testid="save-nudge-settings">
            {savingSettings ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {['pending', 'sent', 'accepted', 'dismissed'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-[#1E2117] text-[#C6E76C]' : 'bg-white text-gray-600 border border-[#E9E9E9] hover:bg-gray-50'}`}
            data-testid={`nudges-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>

      {nudges.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E9E9E9] p-10 text-center text-gray-500 text-sm" data-testid="nudges-empty">
          {tab === 'pending' ? 'No pending nudges — run a scan to check your live items' : `No ${tab} nudges yet`}
        </div>
      ) : (
        <div className="space-y-4">
          {nudges.map(n => {
            const meta = TYPE_META[n.type] || TYPE_META.check_in;
            const Icon = meta.icon;
            return (
              <div key={n.id} className="bg-white rounded-xl border border-[#E9E9E9] p-5" data-testid={`nudge-card-${n.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <button onClick={() => navigate(`/item/${n.item_id}`)} className="text-sm font-semibold text-[#474747] hover:underline" data-testid={`nudge-item-link-${n.id}`}>
                      {n.item_title}
                    </button>
                    <span className="text-xs text-gray-400">• {n.days_live} days live • {n.seller_name}</span>
                  </div>
                  <span className="text-xs text-gray-400">{fmt(n.sent_at || n.created_at)}</span>
                </div>
                {tab === 'pending' ? (
                  <>
                    <Textarea rows={3} value={drafts[n.id] ?? n.message}
                      onChange={(e) => setDrafts({ ...drafts, [n.id]: e.target.value })}
                      className="text-sm" data-testid={`nudge-message-${n.id}`} />
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" disabled={busyId === n.id} onClick={() => approve(n)}
                        className={`font-semibold ${confirmingId === n.id ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117]'}`} data-testid={`nudge-approve-${n.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" /> {confirmingId === n.id ? 'Confirm — Send SMS Now' : 'Approve & Send SMS'}
                      </Button>
                      <Button size="sm" variant="outline" disabled={busyId === n.id} onClick={() => dismiss(n)} data-testid={`nudge-dismiss-${n.id}`}>
                        <X className="w-3.5 h-3.5 mr-1" /> Dismiss
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{n.message}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
